import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';

const DispatchEntryForm = () => {
  const [formData, setFormData] = useState({
    do_id: '',
    dispatch_date: '',
    weight_slip_ref_no: '',
    vehicle_no: '',
    lifted_quantity_mt: '',
    dispatch_amount: '',
    invoice_no: '',
    invoice_date: '',
    invoice_amount: ''
  });
  const [selectedDO, setSelectedDO] = useState(null);
  const [quantityError, setQuantityError] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [commodities, setCommodities] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [warehouses, setWarehouses] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [saleOrders, setSaleOrders] = useState([]);
  const [deliveryOrders, setDeliveryOrders] = useState([]);
  const navigate = useNavigate();

  // Fetch CMA and Commodity dropdowns on mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        // Fetch Commodities
        const commodityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
          credentials: 'include'
        });
        if (commodityRes.ok) {
          const commodityData = await commodityRes.json();
          setCommodities(commodityData.data || []);
        }
        // Fetch Sale Orders
        const saleOrderRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-sale-order`, {
          credentials: 'include'
        });
        if (saleOrderRes.ok) {
          const saleOrderData = await saleOrderRes.json();
          setSaleOrders(saleOrderData.data || []);
        }
        // Fetch Delivery Orders
        const deliveryOrderRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-do`, {
          credentials: 'include'
        });
        if (deliveryOrderRes.ok) {
          const deliveryOrderData = await deliveryOrderRes.json();
          setDeliveryOrders(deliveryOrderData.data || []);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      }
    };
    fetchDropdownData();
  }, []);

  // Fetch all warehouses
  useEffect(() => {
    const fetchWarehouses = async () => {
      try {
        const warehouseRes = await fetch(`${API_BASE_URL}/api/masters/warehouses`, {
          credentials: 'include'
        });
        if (warehouseRes.ok) {
          const warehouseData = await warehouseRes.json();
          setWarehouses(warehouseData.data || []);
        }
      } catch (error) {
        console.error('Error fetching warehouses:', error);
      }
    };
    fetchWarehouses();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'lifted_quantity_mt') {
      validateQuantity(value);
    }
  };

  const validateQuantity = (quantity) => {
    if (selectedDO && quantity && parseFloat(quantity) > parseFloat(selectedDO.remaining_qty_mt)) {
      setQuantityError(`Quantity cannot exceed available DO quantity (${selectedDO.remaining_qty_mt} MT)`);
    } else {
      setQuantityError('');
    }
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));

    if (fieldName === 'do_id') {
      const selected = deliveryOrders.find(d => d.do_id === value);
      setSelectedDO(selected || null);
      setQuantityError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (quantityError) {
      setMessage({ type: 'error', text: 'Please fix the quantity error before submitting' });
      return;
    }
    setLoading(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/sales/dispatch-entry/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        setMessage({ type: 'success', text: '✅ Dispatch Entry saved successfully!' });
        setFormData({
          do_id: '',
          dispatch_date: '',
          weight_slip_ref_no: '',
          vehicle_no: '',
          lifted_quantity_mt: '',
          dispatch_amount: '',
          invoice_no: '',
          invoice_date: '',
          invoice_amount: ''
        });
      } else {
        setMessage({ type: 'error', text: data.error || '❌ Error saving dispatch entry.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '❌ Error saving dispatch entry.' });
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-3 py-1.5">
        <h2 className="text-[15px] font-bold mb-0 text-left">Dispatch Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#15803d] border border-[#15803d] px-2 py-0.5 rounded text-[12px] font-medium hover:bg-[#f0fdf4]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-3 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

            <div>
              <label className="block text-[12px] font-medium mb-1">Delivery Order No</label>
              <SearchableDropdown
                options={deliveryOrders.map(do_order => ({
                  ...do_order,
                  display_name: `${do_order.do_number} (${do_order.remaining_qty_mt} MT Avail)`
                }))}
                value={formData.do_id}
                onChange={val => handleDropdownChange('do_id', val)}
                placeholder="Select Delivery Order"
                displayKey="display_name"
                valueKey="do_id"
                required
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Dispatch Date</label>
              <input
                type="date"
                name="dispatch_date"
                value={formData.dispatch_date}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Weight Slip Ref No</label>
              <input
                type="text"
                name="weight_slip_ref_no"
                value={formData.weight_slip_ref_no}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Vehicle No</label>
              <input
                type="text"
                name="vehicle_no"
                value={formData.vehicle_no}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                required
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Lifted Quantity (MT)</label>
              <input
                type="number"
                name="lifted_quantity_mt"
                value={formData.lifted_quantity_mt}
                onChange={handleChange}
                step="0.001"
                className={`w-full border px-3 py-1.5 text-[13px] bg-white focus:outline-none rounded-none ${
                  quantityError ? 'border-red-500' : 'border-gray-300 focus:border-[#15803d]'
                }`}
                required
                autoComplete="off"
              />
              {quantityError && (
                <div className="text-red-500 text-[12px] mt-1">{quantityError}</div>
              )}
              {selectedDO && !quantityError && (
                <div className="text-gray-500 text-[12px] mt-1">
                  Available quantity: {selectedDO.remaining_qty_mt} MT
                </div>
              )}
            </div>


            <div>
              <label className="block text-[12px] font-medium mb-1">Dispatch Amount (INR)</label>
              <input
                type="number"
                name="dispatch_amount"
                value={formData.dispatch_amount}
                onChange={handleChange}
                step="0.01"
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Invoice No</label>
              <input
                type="text"
                name="invoice_no"
                value={formData.invoice_no}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Invoice Date</label>
              <input
                type="date"
                name="invoice_date"
                value={formData.invoice_date}
                onChange={handleChange}
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium mb-1">Invoice Amount (INR)</label>
              <input
                type="number"
                name="invoice_amount"
                value={formData.invoice_amount}
                onChange={handleChange}
                step="0.01"
                className="w-full border border-gray-300 px-3 py-1.5 text-[13px] bg-white focus:outline-none focus:border-[#15803d] rounded-none"
                autoComplete="off"
              />
            </div>
          </div>
        </div>
        <div className="px-3 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-3 py-1.5 text-[13px] bg-[#15803d] text-white font-semibold tracking-wide rounded border border-[#15803d] hover:bg-[#166534] transition disabled:opacity-60"
            style={{ minWidth: '100px' }}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Dispatch'}
          </button>
          {message && (
            <div className={`mt-2 text-center text-[13px] ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
              {message.text}
            </div>
          )}
        </div>
      </form>
    </div>
  );
};

export default DispatchEntryForm;